<script lang="ts">
import type { Transaction } from '../../lib/types';
import { Badge } from '../common';
import SearchableSelect from '../common/SearchableSelect.svelte';

type Props = {
  transaction: Transaction; // Representative transaction with _groupCount and _groupedIds
  transactionLookup: Map<string, Transaction>; // Fast lookup map for transactions by ID
  categories: string[];
  selectedCategory: string;
  createRule: boolean;
  action: 'confirm' | 'skip';
  onToggleAction: () => void;
  onToggleCreateRule: () => void;
  onCategoryChange: (category: string) => void;
  onCreateCategory: (categoryName: string) => Promise<void>;
};

let {
  transaction,
  transactionLookup,
  categories,
  selectedCategory,
  createRule,
  action,
  onToggleAction,
  onToggleCreateRule,
  onCategoryChange,
  onCreateCategory
}: Props = $props();

// Local state for the category select (needed for binding)
let localCategory = $state(selectedCategory);

// Sync local state with prop changes
$effect(() => {
  localCategory = selectedCategory;
});

// Notify parent when local state changes
$effect(() => {
  if (localCategory !== selectedCategory) {
    onCategoryChange(localCategory);
  }
});

// Use the original description as the merchant name
const merchantName = transaction.displayName || transaction.description;

// Compute grouped transactions using lookup map (O(n) instead of O(n*m))
const groupTransactions = $derived.by(() => {
  if (!transaction._groupedIds || transactionLookup.size === 0) return [transaction];

  // Use the lookup map for instant access (O(1) per transaction)
  const transactions: Transaction[] = [];
  for (const id of transaction._groupedIds) {
    const txn = transactionLookup.get(id);
    if (txn) transactions.push(txn);
  }
  return transactions;
});

// Calculate total amount for the group
const totalAmount = $derived(
  groupTransactions.reduce((sum, t) => sum + t.amount, 0)
);

// Get date range (first to last)
const dateRange = $derived.by(() => {
  if (groupTransactions.length === 0) return '';
  if (groupTransactions.length === 1) {
    return formatDate(groupTransactions[0].date);
  }

  const dates = groupTransactions.map(t => new Date(t.date)).sort((a, b) => a.getTime() - b.getTime());
  const first = dates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const last = dates[dates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  if (first === last) return first;
  return `${first} - ${last}`;
});

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
}

function formatAmount(amount: number): string {
  return `$${Math.abs(amount).toFixed(2)}`;
}
</script>

<div class="group-row" class:skipped={action === 'skip'}>
  <!-- Checkbox -->
  <div class="col-checkbox">
    <input
      type="checkbox"
      checked={action === 'confirm'}
      onchange={onToggleAction}
    />
  </div>

  <!-- Merchant Name -->
  <div class="col-merchant">
    <span class="merchant-name">{merchantName}</span>
  </div>

  <!-- Date Range -->
  <div class="col-date">
    <span>{dateRange}</span>
  </div>

  <!-- Count Badge -->
  <div class="col-count">
    {#if transaction._groupCount && transaction._groupCount > 1}
      <Badge variant="success">
        {#snippet children()}
          {transaction._groupCount}
        {/snippet}
      </Badge>
    {/if}
  </div>

  <!-- Amount -->
  <div class="col-amount">
    <span class="amount" class:negative={totalAmount < 0}>
      {formatAmount(totalAmount)}
    </span>
  </div>

  <!-- Category Select -->
  <div class="col-category">
    <SearchableSelect
      bind:value={localCategory}
      options={categories}
      placeholder="Select category..."
      onCreate={onCreateCategory}
    />
  </div>

  <!-- Create Rule Checkbox -->
  <div class="col-rule">
    <input
      type="checkbox"
      checked={createRule}
      onchange={onToggleCreateRule}
      disabled={action === 'skip'}
    />
  </div>
</div>

<style lang="scss">
.group-row {
  display: grid;
  grid-template-columns: 40px minmax(200px, 2fr) 120px 80px 100px minmax(160px, 1fr) 50px;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--color-border);
  transition: background var(--transition-fast);
  align-items: center;

  &:hover {
    background: var(--color-background-tertiary);
  }

  &.skipped {
    opacity: 0.6;
  }
}

.col-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;

  input[type="checkbox"] {
    cursor: pointer;
    width: 18px;
    height: 18px;
  }
}

.col-merchant {
  min-width: 0;

  .merchant-name {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
  }
}

.col-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.col-count {
  display: flex;
  align-items: center;
  justify-content: center;
}

.col-amount {
  text-align: right;

  .amount {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--color-success);
    white-space: nowrap;

    &.negative {
      color: var(--color-text-primary);
    }
  }
}

.col-category {
  display: flex;
  align-items: center;
  min-width: 160px;
}

.col-rule {
  display: flex;
  align-items: center;
  justify-content: center;

  input[type="checkbox"] {
    cursor: pointer;
    width: 18px;
    height: 18px;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}
</style>
